import { Spinner } from '@frontend/components/common';
import { SensorLockerStatus } from '@frontend/device/types';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage } from 'react-intl';

import LargeSlotCard from '../../../components/slot-cards/large-slot-card.component';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { ProductCard } from '../browse-products-by-categories/product-card/product-card.component';
import { useLendingReturnAvailableDetail } from './lending-return-available-detail.controller';

const ID = 'lending-return-available-detail';

const LendingReturnAvailableDetail = () => {
    const viewProps = useLendingReturnAvailableDetail();

    if (viewProps.isLoading) {
        return (
            <WizardScreenContainer id={ID}>
                <div className='flex size-full items-center justify-center'>
                    <Spinner className='text-border-default fill-brand-primary size-12' />
                </div>
            </WizardScreenContainer>
        );
    }

    const submitLabel =
        viewProps.stock && viewProps.slot && !viewProps.requiresAuthentication ? (
            <FormattedMessage
                id='kiosk.manage-spot.view-content-slot-detail.open-slot'
                defaultMessage='Open SLOT {name}'
                values={{ name: viewProps.slot.display }}
            />
        ) : (
            CommonMessage.BUTTONS.CONTINUE
        );

    return (
        <WizardScreenContainer
            id={ID}
            footer={{
                onBack: viewProps.onBack,
                onSubmit: viewProps.onSubmit,
                submitDisabled: viewProps.submitDisabled,
                submitLabel
            }}
            loadingModal={{ show: viewProps.isProcessing || viewProps.isAuthenticating || viewProps.isIdentifying }}
            errorModal={{
                show: viewProps.authenticationError,
                title: CommonMessage.ERRORS.UNRECOGNIZED_CODE,
                message: (
                    <FormattedMessage
                        id='kiosk.wizard.lending-return-available-detail.authentication-failed'
                        defaultMessage='We could not authenticate you. Please try again.'
                        description='Error shown when badge authentication fails on lending return available detail'
                    />
                ),
                onClose: viewProps.resetAuthenticationError
            }}>
            <div className='flex w-full flex-col gap-6'>
                {viewProps.error ? (
                    <div className='text-foreground-default text-center'>
                        <FormattedMessage
                            id='kiosk.wizard.lending-return-available-detail.load-error'
                            defaultMessage='We could not prepare access to this equipment. Please try again.'
                            description='Error shown when stock cannot be resolved or the audit transaction cannot be created'
                        />
                    </div>
                ) : (
                    <>
                        <div className='flex flex-col gap-3'>
                            <p className='text-foreground-default font-medium'>
                                <FormattedMessage
                                    id='kiosk.wizard.lending-return-available-detail.selected-equipment'
                                    defaultMessage='Selected equipment'
                                    description='Title above the selected available equipment'
                                />
                            </p>
                            <ProductCard
                                variant='attributes'
                                product={viewProps.product ?? undefined}
                                equipment={viewProps.equipment ?? undefined}
                            />
                        </div>

                        {viewProps.stock && viewProps.slot ? (
                            <div className='flex flex-col gap-3'>
                                <p className='text-foreground-default font-medium'>
                                    {viewProps.requiresAuthentication ? (
                                        <FormattedMessage
                                            id='kiosk.wizard.lending-return-available-detail.shared-slot'
                                            defaultMessage='This SLOT contains other items. Scan your badge to authenticate before opening it.'
                                            description='Explanation shown when an available equipment shares its slot with other stock'
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id='kiosk.wizard.lending-return-available-detail.equipment-in-slot'
                                            defaultMessage='This equipment is already stored in the following SLOT.'
                                            description='Explanation shown when an available equipment is already in the distriSPOT'
                                        />
                                    )}
                                </p>
                                <LargeSlotCard
                                    slot={viewProps.slot}
                                    status={SensorLockerStatus.CLOSED}
                                />
                            </div>
                        ) : (
                            <div className='bg-background-surface border-border-default text-foreground-default rounded-xl border p-4'>
                                <FormattedMessage
                                    id='kiosk.wizard.lending-return-available-detail.not-in-stock'
                                    defaultMessage='This equipment is not registered in this distriSPOT. Continue to return it.'
                                    description='Explanation shown when available equipment has no stock in the current distriSPOT'
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </WizardScreenContainer>
    );
};

export default LendingReturnAvailableDetail;
